// ===========================================
// Admin Route Guard (staff permission enforcement)
// ===========================================
// The /admin parent route admits any staff user (ProtectedRoute's
// coarse-grained fallback), and AdminLayout only filters the NAV — so before
// this guard, a staff user could reach every /admin page by URL and watch its
// queries 403. This guard closes that gap centrally: for staff users on an
// /admin path it resolves the page's permission resource from one map and
// renders an access-denied state instead of the page when the resource isn't
// granted. Full ADMINs and superAdmins are never gated. The backend remains
// the security boundary — this is defence-in-depth + honest UX.

import { ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';

// ===========================================
// PATH → PERMISSION RESOURCE MAP
// ===========================================

/**
 * First path segment under /admin → the permission resource the page needs
 * (checked with canAny — any action on the resource grants page access,
 * matching how AdminLayout filters the nav).
 *
 * `null` = personal page, any staff user may open it.
 * A missing key = unknown/new route → FAIL-OPEN (page renders; its queries
 * still 403 server-side). Fail-open is deliberate: an unmapped page must
 * degrade to today's behaviour, not lock legitimate staff out. Add new admin
 * routes here when adding them to App.tsx.
 *
 * Deeper segments inherit the first segment's resource (classes/:id →
 * 'classes'; reports/revenue → 'reports'; newsletters/new → 'communications_newsletters').
 */
/**
 * Marks a page that only the OWNER tier (the ADMIN role) may open, whatever a staff role
 * holds: the twin of the server's `ownerOnly` (roles plan R1). Not a catalogue resource.
 */
export const OWNER_ONLY = '__owner_only__';

export const ADMIN_ROUTE_RESOURCES: Record<string, string | string[] | null> = {
  '': 'dashboard',
  // Standalone
  messages: 'messages',
  calendar: 'scheduling',
  'meet-and-greet': 'meet_and_greet',
  'new-students': 'meet_and_greet',
  enquiries: 'enquiries',
  // Events
  events: 'events',
  'save-the-dates': 'events',
  // Both were UNMAPPED until 2026-09-16 (found by the website-removal /qa):
  // the guard is fail-open on an unknown first segment, so any staff user could
  // open these two pages. Same resource as their nav section.
  'permission-slips': 'events',
  'event-pricing-structures': 'events',
  // School setup
  terms: 'school_setup',
  locations: 'school_setup',
  rooms: 'school_setup',
  // Class config
  'age-groups': 'class_config',
  durations: 'class_config',
  instruments: 'class_config',
  'class-types': 'class_config',
  'program-levels': 'class_config',
  streams: 'class_config',
  pieces: 'class_config',
  // Gamification
  achievements: 'gamification',
  'student-progress': 'gamification',
  leaderboard: 'gamification',
  // People
  teachers: 'people_teachers',
  families: 'people_families',
  students: 'people_students',
  'settling-in': 'student_onboarding',
  // Scheduling
  classes: 'scheduling',
  'extra-lessons': 'scheduling',
  absences: 'scheduling',
  'hybrid-pre-booking': 'scheduling',
  // Operations
  invoices: 'operations_invoices',
  subscriptions: 'operations_subscriptions',
  'pricing-config': 'operations_pricing',
  products: 'operations_pricing',
  'custom-discounts': 'operations_pricing',
  'payment-settings': 'operations_payments',
  // Reports
  reports: 'reports',
  // Communications
  newsletters: 'communications_newsletters',
  'custom-emails': 'communications_emails',
  'email-sequences': 'communications_emails',
  'custom-sms': 'communications_emails',
  'email-log': 'communications_emails',
  'sms-settings': 'communications_settings',
  'system-emails': 'communications_settings',
  'email-settings': 'communications_settings',
  // System
  settings: 'system_settings',
  // Roles & Staff: owner only (roles plan R1/R9; `roles_management` left the catalogue).
  roles: OWNER_ONLY,
  integrations: 'integrations',
  'google-drive': 'integrations',
  // Xero needs integrations AND invoices (roles plan 5.6): an array means ALL are required.
  xero: ['integrations', 'operations_invoices'],
  'import-export': 'import_export',
  'security-audit': 'security_audit',
  // Personal pages — any staff user
  account: null,
  notifications: null,
};

/**
 * Deeper paths that are owner-only although their first segment is not (roles slice 2, V27): the
 * map above resolves by the FIRST segment only, so `reports/retro-edits` would otherwise inherit
 * `reports`. Matched on the path's leading segments, so a sub-path of one is owner-only too.
 */
export const OWNER_ONLY_SUBPATHS: readonly string[] = ['reports/retro-edits', 'reports/family-credit'];

/**
 * Report sub-paths gated by their OWN permission, not `reports` (#374 Phase 2: a role holding
 * registration_fees.read but no reports.read opens the registration fees report). Checked before
 * the first-segment map, like the owner-only paths.
 */
export const PERMISSION_SUBPATHS: Readonly<Record<string, string>> = {
  'reports/registration-fees': 'registration_fees',
};

/**
 * Resolve the permission resource for an /admin sub-path.
 * Returns: a resource string (must be granted), null (personal page — always
 * allowed), or undefined (unmapped route — fail-open).
 */
export function getAdminRouteResource(adminSubPath: string): string | string[] | null | undefined {
  const segments = adminSubPath.split('/').filter(Boolean);
  if (OWNER_ONLY_SUBPATHS.some((p) => p.split('/').every((s, i) => segments[i] === s))) {
    return OWNER_ONLY;
  }
  for (const [path, resource] of Object.entries(PERMISSION_SUBPATHS)) {
    if (path.split('/').every((s, i) => segments[i] === s)) return resource;
  }
  // The Email Log moved to Communications; the old path only redirects, and must be judged by the
  // permission of where it lands, not by `reports`.
  if (segments[0] === 'reports' && segments[1] === 'email-log') return 'communications_emails';
  const firstSegment = adminSubPath.split('/')[0] ?? '';
  return ADMIN_ROUTE_RESOURCES[firstSegment];
}

// ===========================================
// GUARD COMPONENT
// ===========================================

interface AdminRouteGuardProps {
  children: ReactNode;
}

export default function AdminRouteGuard({ children }: AdminRouteGuardProps) {
  const { canAny, isStaff, isSuperAdmin, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const location = useLocation();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  // Only staff users are gated, and only on /admin paths (AdminLayout is
  // shared by /teacher and /parent shells).
  const pathParts = location.pathname.split('/'); // ['', slug, 'admin', ...rest]
  const isAdminPath = pathParts[2] === 'admin';
  if (!isStaff || isSuperAdmin || !isAdminPath) {
    return <>{children}</>;
  }

  const adminSubPath = pathParts.slice(3).join('/');
  const resource = getAdminRouteResource(adminSubPath);
  const allowed =
    resource === OWNER_ONLY
      ? isOwner
      : resource === null || resource === undefined
        ? true
        : Array.isArray(resource)
          ? resource.every((r) => canAny(r))
          : canAny(resource);

  if (allowed) {
    return <>{children}</>;
  }

  // Escape hatch must itself be reachable: a staff role WITHOUT the
  // 'dashboard' resource would loop on a "Back to Dashboard" CTA (the index
  // page is dashboard-gated too) — send them to their account page instead,
  // which is a personal route every staff user can open.
  const slug = schoolSlug ?? 'music-n-me';
  const escape = canAny('dashboard')
    ? { to: `/${slug}/admin`, label: 'Back to Dashboard' }
    : { to: `/${slug}/admin/account`, label: 'Go to My Account' };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <Box sx={{ maxWidth: 480, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 56, color: 'text.secondary', opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
          You don't have access to this page
        </Typography>
        <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
          Your staff role doesn't include permission for this area. Ask your {ownerTitle} if you think
          you should have access.
        </Alert>
        <Button component={RouterLink} to={escape.to} variant="contained">
          {escape.label}
        </Button>
      </Box>
    </Box>
  );
}
